import { ArrayHelper } from '@helpers/ArrayHelper'
import { ViewOptions } from '@models/ViewOptions'
import { LegendItem } from '@models/api/simulation/legend/LegendItem'
import { PersistentDataService } from '@services/PersistentDataService'
import { defineStore } from 'pinia'
import { Feature, Geometry } from 'geojson'
import { MapOptions } from 'src/models/MapOptions'
import { ref } from 'vue'
import { BurnedAreaItem } from '@models/BurnedAreaItem'
import { Basemap } from '@models/Basemap'
import i18n from 'src/i18n'

const defaultLayersOptions: Partial<MapOptions> = {
  isBurnedAreasVisible: false,
  isFuelModelsVisible: false,
  isWUIVisible: false
}

const defaultMapOptions: MapOptions =
  MapOptions.getDefaultSimulationMapOptions()

const defaultViewOptions: ViewOptions = {
  isLeyendaVisible: true
}

export const useSimulationMapStore = defineStore('simulationMap', () => {
  const viewOptions = ref<ViewOptions>(defaultViewOptions)
  const mapOptions = ref<MapOptions>({
    ...defaultMapOptions,
    ...defaultLayersOptions
  })
  const legendItems = ref<LegendItem[]>([])
  const legendTitle = ref<string>()
  const figureToSimulate = ref<Feature<Geometry, BurnedAreaItem>>()
  const opacity = mapOptions.value.opacity
  const basemaps: Basemap[] = [
    // { type: 'raster', title: 'El que no le gusta a Alberto', image: 'map-relief.jpg', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Shaded_Relief/MapServer/tile/{z}/{y}/{x}', attribution: 'Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ' },
    // { type: 'vector', title: 'World Basemap', image: 'map-satellite.jpg', url: 'https://basemaps.arcgis.com/arcgis/rest/services/World_Basemap_v2/VectorTileServer', attribution: '' },
    {
      id: 'relieve',
      type: 'vectorBasemap',
      title: i18n.t('header.panels.basemaps.relief'),
      image: 'map-relief-new.jpg',
      arcgisId: 'terrain',
      key: 'e86bdf88c15b4e9391d75363909c2677',
      attribution: ''
    },
    // { id: 'coastline', type: 'vectorBasemap', title: 'Coastline', image: 'map-light-gray-labels.jpg', key: '7ac23232c0f34b16ba1f75c0fe8bb13a', attribution: '' },
    {
      id: 'light-gray',
      type: 'vectorBasemap',
      title: i18n.t('header.panels.basemaps.grayScale'),
      image: 'map-light-gray-new.jpg',
      arcgisId: 'gray-vector',
      key: 'ArcGIS:LightGray:Base',
      attribution: ''
    },
    // { id: 'light-gray-labels', type: 'vectorBasemap', title: 'Light Gray (labels)', image: 'map-light-gray-labels.jpg', key: 'ArcGIS:LightGray:Labels', attribution: '' },
    // { type: 'tiledMap', title: 'Relieve2', image: 'map-light-gray-labels.jpg', url: 'https://services.arcgisonline.com/arcgis/rest/services/Elevation/World_Hillshade/MapServer', attribution: '' },
    {
      id: 'imagery',
      type: 'vectorBasemap',
      title: i18n.t('header.panels.basemaps.satelite'),
      dark: true,
      image: 'map-imagery.jpg',
      arcgisId: 'hybrid',
      key: 'ArcGIS:Imagery:Standard',
      attribution: ''
    },
    {
      id: 'openstreetmap',
      type: 'tileLayer',
      title: 'OpenStreetMap',
      arcgisId: 'osm',
      image: 'map-openstreetmap.jpg',
      url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
      attribution: ''
    },
    {
      id: 'IGNBase',
      type: 'WMTS',
      title: 'IGNBase',
      activeLayer: 'IGNBase-gris',
      url: 'https://www.ign.es/wmts/ign-base',
      attribution: `<a href="https://www.ign.es/" target="_blank">Instituto Geográfico Nacional</a>`,
      srcImage:
        'https://www.ign.es/wmts/ign-base?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&LAYER=IGNBase-gris&STYLE=default&FORMAT=image%2Fjpeg&TILEMATRIXSET=GoogleMapsCompatible&TILEMATRIX=6&TILEROW=24&TILECOL=31'
    },
    {
      id: 'OrthoIGNBase',
      type: 'WMTS',
      title: 'OrthoIGNBase',
      activeLayer: 'MTN',
      url: 'https://www.ign.es/wmts/mapa-raster',
      attribution: `<a href="https://www.ign.es/" target="_blank">Instituto Geográfico Nacional</a>`,
      srcImage:
        'https://www.ign.es/wmts/mapa-raster?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&LAYER=MTN&STYLE=default&FORMAT=image%2Fjpeg&TILEMATRIXSET=GoogleMapsCompatible&TILEMATRIX=6&TILEROW=24&TILECOL=31'
    },
    {
      id: 'LidarIGNBase',
      type: 'WMTS',
      title: 'LidarIGNBase',
      image: 'map-capaLidar.png',
      url: 'https://wmts-mapa-lidar.idee.es/lidar?request=GetCapabilities&service=WMTS',
      attribution: `<a href="https://www.ign.es/" target="_blank">Instituto Geográfico Nacional</a>`,
      srcImage:
        'https://www.ign.es/wmts/mapa-raster?SERVICE=WMTS&VERSION=1.0.0&REQUEST=GetTile&LAYER=MTN&STYLE=default&FORMAT=image%2Fjpeg&TILEMATRIXSET=GoogleMapsCompatible&TILEMATRIX=6&TILEROW=24&TILECOL=31'
    }
  ]

  const setFigureToSimulate = (figure: Feature<Geometry, BurnedAreaItem>) => {
    figureToSimulate.value = figure
  }

  function setDefaultLayerOptions() {
    mapOptions.value = {
      ...mapOptions.value,
      ...defaultLayersOptions
    }
  }

  function setDefaultOptions() {
    viewOptions.value = defaultViewOptions
    mapOptions.value = {
      ...mapOptions.value,
      ...defaultMapOptions
    }
  }

  const getFigureToSimulate = () => {
    const result = figureToSimulate.value
    figureToSimulate.value = undefined
    return result
  }

  const updateLegendItems = (items: LegendItem[]) => {
    legendItems.value = ArrayHelper.getUniqueItems(items)
  }

  const pushLegendItems = (items: LegendItem[], mode?: 'start' | 'end') => {
    const newLegendItems = legendItems.value
    // push on the start of the array
    if (mode === 'start') {
      newLegendItems.unshift(...items)
    } else {
      newLegendItems.push(...items)
    }
    updateLegendItems(newLegendItems)
  }

  const SET_USER_MAP_OPTIONS = () => {
    mapOptions.value = {
      ...mapOptions.value,
      ...PersistentDataService.getOptions('simulationMapOptions'),
      // Is undefined because is pending to be implemented #6317
      isMapOverviewVisible: undefined
    }
  }

  const SET_USER_VIEW_OPTIONS = () => {
    viewOptions.value = {
      ...viewOptions.value,
      ...PersistentDataService.getOptions('simulationViewOptions')
    }
  }

  return {
    viewOptions,
    mapOptions,
    opacity,
    legendItems,
    setFigureToSimulate,
    getFigureToSimulate,
    updateLegendItems,
    pushLegendItems,
    legendTitle,
    SET_USER_MAP_OPTIONS,
    SET_USER_VIEW_OPTIONS,
    setDefaultOptions,
    setDefaultLayerOptions,
    basemaps
  }
})
